import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Pagination, TextField, MenuItem, InputAdornment, Icon, CircularProgress, Chip } from "@mui/material";
import { useICCCColors, ICCC_COLORS as ICCC_FALLBACK } from "./theme";
import {
  BOTTOM_ROW_HEIGHT,
  DEVICE_PAGE_SIZE,
  categoryMeta,
  normalizeCategoryCode,
  severityToHealth,
  categoryEqualsFilter,
  resolveCategoryCode,
} from "../model/constants";
import { useGetDevicesQuery, useGetCategoriesQuery } from "features/icccApiSlice";

const HEALTH_LABEL = { healthy: "Healthy", warning: "Warning", critical: "Critical", unknown: "Unknown" };

const StatusPill = ({ severity }) => {
  const health = severityToHealth(severity);
  // Status colours are theme-invariant — reuse the constants.
  const color = ICCC_FALLBACK[health] || ICCC_FALLBACK.unknown;
  return (
    <span style={{ background: color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {HEALTH_LABEL[health]}
    </span>
  );
};

StatusPill.propTypes = { severity: PropTypes.number };

const COLUMNS = ["Device #", "Device Name", "Category", "Status", "IP Address", "Location"];

// When we filter client-side — free-text search (backend search is case-sensitive
// and single-column) or the "Unknown" no-category bucket the backend can't express
// as IS NULL — fetch up to this many rows and filter in the browser.
const CLIENT_FETCH_CAP = 1000;

export default function DeviceStatusTable({ groupFilter, onClearGroupFilter, groups = [], onRowClick }) {
  const ICCC_COLORS = useICCCColors();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Groups");
  const { data: categories } = useGetCategoriesQuery();

  const th = {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    fontWeight: 600,
    color: ICCC_COLORS.textSecondary,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "7px 10px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    color: ICCC_COLORS.textSecondary,
  };

  useEffect(() => {
    setPage(0);
  }, [groupFilter]);

  const selected = groupFilter || (categoryFilter !== "All Groups" ? categoryFilter : null);
  const resolvedCat = resolveCategoryCode(selected, categories);
  // "Unknown" is the synthetic bucket of devices with no/unmapped category.
  const isUnknownBucket = !!selected && resolvedCat === "UNKNOWN";
  const hasSearch = !!search && search.trim() !== "";
  // Free-text search and the Unknown bucket are filtered in the browser (the
  // backend search is case-sensitive / single-column and can't do IS NULL), so we
  // fetch a capped page and filter client-side. Otherwise stay fully server-side.
  const clientMode = isUnknownBucket || hasSearch;

  const body = useMemo(() => {
    return {
      currentPage: clientMode ? 0 : page,
      pageSize: clientMode ? CLIENT_FETCH_CAP : DEVICE_PAGE_SIZE,
      sortDirection: "asc",
      sortBy: "id",
      sortDataType: "integer",
      search: "",
      advancedFilters: isUnknownBucket ? [] : categoryEqualsFilter(resolvedCat),
    };
  }, [page, clientMode, isUnknownBucket, resolvedCat]);

  const { data, isFetching, isError } = useGetDevicesQuery(body);

  const { rows, total } = useMemo(() => {
    const raw = data?.rows || [];
    if (!clientMode) return { rows: raw, total: data?.total || 0 };
    let filtered = raw;
    if (isUnknownBucket) {
      filtered = filtered.filter((r) => normalizeCategoryCode(r.category) === "UNKNOWN");
    }
    if (hasSearch) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter((r) =>
        [r.nodeName, r.ipAddress, r.category, categoryMeta(r.category).label, r.location, r.nodeId, r.id].some(
          (v) => v != null && String(v).toLowerCase().includes(q)
        )
      );
    }
    return {
      rows: filtered.slice(page * DEVICE_PAGE_SIZE, (page + 1) * DEVICE_PAGE_SIZE),
      total: filtered.length,
    };
  }, [data, clientMode, isUnknownBucket, hasSearch, search, page]);

  const totalPages = Math.max(1, Math.ceil(total / DEVICE_PAGE_SIZE));
  const showFrom = rows.length === 0 ? 0 : page * DEVICE_PAGE_SIZE + 1;
  const showTo = Math.min((page + 1) * DEVICE_PAGE_SIZE, total);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: ICCC_COLORS.textPrimary,
      "& fieldset": { borderColor: ICCC_COLORS.cardBorder },
      "&:hover fieldset": { borderColor: ICCC_COLORS.accent },
    },
    "& input::placeholder": { color: ICCC_COLORS.textDim, opacity: 1 },
    "& .MuiSvgIcon-root": { color: ICCC_COLORS.textSecondary },
  };

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        height: BOTTOM_ROW_HEIGHT,
        display: "flex",
        flexDirection: "column",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary }}>
          Device Status
        </MDTypography>
        {groupFilter && (
          <Chip
            size="small"
            label={`Group: ${categoryMeta(groupFilter).label}`}
            onDelete={onClearGroupFilter}
            deleteIcon={<Icon sx={{ color: ICCC_COLORS.accent + " !important" }}>close</Icon>}
            sx={{
              bgcolor: ICCC_COLORS.accent + "22",
              color: ICCC_COLORS.accent,
              fontWeight: 600,
              border: `1px solid ${ICCC_COLORS.accent}55`,
            }}
          />
        )}
      </MDBox>

      <MDBox display="flex" gap={1} mb={1}>
        <TextField
          size="small"
          placeholder="Search devices..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1, ...inputSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small" sx={{ color: ICCC_COLORS.textMuted }}>
                  search
                </Icon>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 150, ...inputSx }}
          SelectProps={{
            MenuProps: { PaperProps: { sx: { bgcolor: ICCC_COLORS.cardBg2, color: ICCC_COLORS.textPrimary } } },
          }}
          disabled={!!groupFilter}
        >
          {[
            { code: "All Groups", label: "All Groups" },
            ...groups
              .filter((g) => (g.total || 0) > 0)
              .map((g) => ({ code: g.code, label: categoryMeta(g.code).label })),
          ].map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </MDBox>

      <MDBox
        sx={{
          flex: 1,
          overflow: "auto",
          border: `1px solid ${ICCC_COLORS.divider}`,
          borderRadius: 1,
          position: "relative",
          bgcolor: ICCC_COLORS.cardBg2,
        }}
      >
        {isFetching && (
          <MDBox
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: ICCC_COLORS.hoverBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <CircularProgress size={28} sx={{ color: ICCC_COLORS.accent }} />
          </MDBox>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: ICCC_COLORS.cardBg2, zIndex: 1 }}>
            <tr>
              {COLUMNS.map((h) => (
                <th key={h} style={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isFetching && (
              <tr>
                <td colSpan={COLUMNS.length} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted, padding: 24 }}>
                  {isError ? "Unable to load devices" : "No devices match the current filters"}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id ?? r.nodeId}
                style={{ transition: "background 80ms ease", cursor: onRowClick ? "pointer" : "default" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = ICCC_COLORS.hoverBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                onClick={() => onRowClick && onRowClick(r)}
              >
                <td style={td}>{r.nodeId ?? r.id}</td>
                <td style={td}>{r.nodeName || "—"}</td>
                <td style={td}>{r.category || "—"}</td>
                <td style={td}>
                  <StatusPill severity={r.severity} />
                </td>
                <td style={td}>{r.ipAddress || "—"}</td>
                <td style={td}>{r.location || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textMuted }}>
          Showing {showFrom}-{showTo} of {total.toLocaleString("en-IN")} devices
        </MDTypography>
        <Pagination
          count={totalPages}
          page={page + 1}
          size="small"
          onChange={(_, p) => setPage(p - 1)}
          siblingCount={1}
          boundaryCount={1}
          showFirstButton
          showLastButton
          sx={{
            "& .MuiPaginationItem-root": { color: ICCC_COLORS.textSecondary, borderColor: ICCC_COLORS.cardBorder },
            "& .MuiPaginationItem-root.Mui-selected": {
              bgcolor: ICCC_COLORS.accent + "33",
              color: ICCC_COLORS.accent,
              borderColor: ICCC_COLORS.accent,
            },
            "& .MuiPaginationItem-root:hover": { bgcolor: ICCC_COLORS.hoverBg },
          }}
        />
      </MDBox>
    </MDBox>
  );
}

DeviceStatusTable.propTypes = {
  groupFilter: PropTypes.string,
  onClearGroupFilter: PropTypes.func,
  groups: PropTypes.array,
  onRowClick: PropTypes.func,
};
